import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  startTransition,
  useState,
  Suspense,
  lazy,
} from "react";
import type { Socket } from "socket.io-client";
import { useNavigate } from "react-router-dom";
import apiClient, {
  getRealtimeBaseUrl,
  refreshAuthenticatedSession,
} from "@shared/services/apiClient";
import { useAuth } from "@shared/context/AuthContext";

const CriticalNotificationModal = lazy(
  () => import("./CriticalNotificationModal"),
);
export type NotificationConnectionState =
  | "connecting"
  | "connected"
  | "disconnected";

export interface UnifiedNotification {
  notification_id: string;
  user_id: string;
  organization_id?: string | null;
  type: string;
  category?: string | null;
  title: string;
  message: string;
  priority: "critical" | "high" | "medium" | "low";
  is_read: boolean;
  is_dismissed: boolean;
  action_url?: string | null;
  action_required?: boolean;
  related_entity?: string | null;
  related_id?: string | null;
  ai_priority_score?: number | null;
  created_date?: string;
  read_date?: string | null;
  dismissed_date?: string | null;
}

interface NotificationCategoryCatalogEntry {
  category: string;
  group_name: string;
  legacy_type: "alert" | "warning" | "info" | "success";
  default_severity: "critical" | "warning" | "info";
  default_dedup_window_s: number;
  default_escalation_min: number;
  is_report_category: boolean;
  is_active: boolean;
  sort_order: number;
}

interface UnifiedNotificationsContextValue {
  notifications: UnifiedNotification[];
  unreadCount: number;
  criticalCount: number;
  countsByCategory: Record<string, number>;
  categoryCatalog: NotificationCategoryCatalogEntry[];
  reportCategories: string[];
  isLoading: boolean;
  error: string | null;
  connectionState: NotificationConnectionState;
  refresh: () => Promise<void>;
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  dismiss: (notificationId: string) => Promise<void>;
  remove: (notificationId: string) => Promise<void>;
}

const UnifiedNotificationsContext =
  createContext<UnifiedNotificationsContextValue | null>(null);

type NotificationBootstrapPayload = {
  notifications: UnifiedNotification[];
  categoryCatalog: NotificationCategoryCatalogEntry[];
};

const notificationBootstrapPromises = new Map<
  string,
  Promise<NotificationBootstrapPayload>
>();

const DEBUG_FORCE_WS_AUTH_ERROR_ONCE_KEY =
  "debug.forceWsAuthErrorOnce";
const INITIAL_SSE_FALLBACK_DELAY_MS = 3000;
const MAX_SSE_FALLBACK_DELAY_MS = 60000;

function resolveSocketBaseUrl(): string {
  return getRealtimeBaseUrl();
}

function normalizeRealtimeBaseUrl(baseUrl: string): string {
  return baseUrl.replace(/\/$/, "");
}

function consumeDebugForcedSocketToken(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const isLocalProbeHost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1" ||
    window.location.hostname.endsWith(".localhost");

  if (!isLocalProbeHost) {
    return undefined;
  }

  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get("debugWsAuthErrorOnce") === "1") {
      url.searchParams.delete("debugWsAuthErrorOnce");
      window.history.replaceState(
        window.history.state,
        "",
        `${url.pathname}${url.search}${url.hash}`,
      );
      return "debug-force-mismatched-user";
    }

    if (
      window.sessionStorage.getItem(DEBUG_FORCE_WS_AUTH_ERROR_ONCE_KEY) !== "1"
    ) {
      return undefined;
    }

    window.sessionStorage.removeItem(DEBUG_FORCE_WS_AUTH_ERROR_ONCE_KEY);
    return "debug-force-mismatched-user";
  } catch {
    return undefined;
  }
}

function normalizeNotification(input: any): UnifiedNotification | null {
  if (!input || typeof input !== "object") return null;
  const notificationId =
    typeof input.notification_id === "string"
      ? input.notification_id
      : typeof input.id === "string"
        ? input.id
        : null;
  const userId =
    typeof input.user_id === "string"
      ? input.user_id
      : typeof input.userId === "string"
        ? input.userId
        : "";
  if (!notificationId) return null;

  const priority = String(input.priority || "medium").toLowerCase();
  const normalizedPriority: UnifiedNotification["priority"] =
    priority === "critical" || priority === "high" || priority === "low"
      ? (priority as UnifiedNotification["priority"])
      : "medium";
  const rawType =
    typeof input.notification_type === "string"
      ? input.notification_type
      : typeof input.type === "string"
        ? input.type
        : "info";

  return {
    notification_id: notificationId,
    user_id: userId,
    organization_id:
      typeof input.organization_id === "string" ? input.organization_id : null,
    type: String(rawType),
    category:
      typeof input.category === "string" && input.category.length > 0
        ? input.category
        : null,
    title: String(input.title || "Notification"),
    message: String(input.message || ""),
    priority: normalizedPriority,
    is_read: Boolean(input.is_read),
    is_dismissed: Boolean(input.is_dismissed),
    action_url:
      typeof input.action_url === "string"
        ? input.action_url
        : typeof input.actionUrl === "string"
          ? input.actionUrl
          : null,
    action_required: Boolean(input.action_required),
    related_entity:
      typeof input.related_entity === "string" ? input.related_entity : null,
    related_id: typeof input.related_id === "string" ? input.related_id : null,
    ai_priority_score:
      typeof input.ai_priority_score === "number"
        ? input.ai_priority_score
        : null,
    created_date:
      typeof input.created_date === "string"
        ? input.created_date
        : typeof input.created_at === "string"
          ? input.created_at
          : undefined,
    read_date: typeof input.read_date === "string" ? input.read_date : null,
    dismissed_date:
      typeof input.dismissed_date === "string" ? input.dismissed_date : null,
  };
}

function mergeNotifications(
  current: UnifiedNotification[],
  incoming: UnifiedNotification,
): UnifiedNotification[] {
  const index = current.findIndex(
    (item) => item.notification_id === incoming.notification_id,
  );
  if (index === -1) {
    return [incoming, ...current].sort((a, b) => {
      const aTs = new Date(a.created_date || 0).getTime();
      const bTs = new Date(b.created_date || 0).getTime();
      return bTs - aTs;
    });
  }

  const next = [...current];
  next[index] = { ...next[index], ...incoming };
  return next;
}

function getNotificationsArray(payload: any): UnifiedNotification[] {
  const envelope = payload?.data ?? payload;
  const raw =
    (Array.isArray(envelope?.notifications) && envelope.notifications) ||
    (Array.isArray(envelope?.items) && envelope.items) ||
    (Array.isArray(envelope) && envelope) ||
    [];
  return raw
    .map((entry: unknown) => normalizeNotification(entry))
    .filter((entry: UnifiedNotification | null): entry is UnifiedNotification =>
      Boolean(entry),
    );
}

function severityRank(priority: UnifiedNotification["priority"]): number {
  switch (priority) {
    case "critical":
      return 0;
    case "high":
      return 1;
    case "medium":
      return 2;
    case "low":
      return 3;
    default:
      return 4;
  }
}

function sortNotifications(
  items: UnifiedNotification[],
): UnifiedNotification[] {
  return [...items].sort((a, b) => {
    const rankDelta = severityRank(a.priority) - severityRank(b.priority);
    if (rankDelta !== 0) return rankDelta;
    return (
      new Date(b.created_date || 0).getTime() -
      new Date(a.created_date || 0).getTime()
    );
  });
}

function normalizeCategoryCatalog(
  payload: unknown,
): NotificationCategoryCatalogEntry[] {
  const rows = Array.isArray(payload) ? payload : [];
  return rows
    .filter(
      (entry): entry is Record<string, unknown> =>
        Boolean(entry && typeof entry === "object") &&
        typeof entry.category === "string",
    )
    .map((entry) => ({
      category: String(entry.category),
      group_name: String(entry.group_name || "System"),
      legacy_type:
        entry.legacy_type === "alert" ||
        entry.legacy_type === "warning" ||
        entry.legacy_type === "success"
          ? entry.legacy_type
          : "info",
      default_severity:
        entry.default_severity === "critical" ||
        entry.default_severity === "warning"
          ? entry.default_severity
          : "info",
      default_dedup_window_s: Number(entry.default_dedup_window_s || 0),
      default_escalation_min: Number(entry.default_escalation_min || 0),
      is_report_category: Boolean(entry.is_report_category),
      is_active: Boolean(entry.is_active),
      sort_order: Number(entry.sort_order || 0),
    }));
}

async function fetchNotificationsSnapshot(
  notificationsLimit: number,
  notificationsTimeoutMs: number,
): Promise<UnifiedNotification[]> {
  const response = await apiClient.get("/notifications", {
    params: {
      limit: notificationsLimit,
      offset: 0,
      is_dismissed: false,
    },
    timeout: notificationsTimeoutMs,
  });

  return sortNotifications(getNotificationsArray(response.data));
}

async function fetchCategoryCatalogSnapshot(): Promise<
  NotificationCategoryCatalogEntry[]
> {
  const response = await apiClient.get("/notifications/catalog/categories");
  const payload = response.data?.data ?? response.data ?? [];
  return normalizeCategoryCatalog(payload);
}

async function getOrStartNotificationBootstrap(
  userId: string,
  notificationsLimit: number,
  notificationsTimeoutMs: number,
): Promise<NotificationBootstrapPayload> {
  const inFlight = notificationBootstrapPromises.get(userId);
  if (inFlight) {
    return inFlight;
  }

  const request = Promise.all([
    fetchNotificationsSnapshot(notificationsLimit, notificationsTimeoutMs),
    fetchCategoryCatalogSnapshot().catch(() => []),
  ])
    .then(([notifications, categoryCatalog]) => ({
      notifications,
      categoryCatalog,
    }))
    .finally(() => {
      notificationBootstrapPromises.delete(userId);
    });

  notificationBootstrapPromises.set(userId, request);
  return request;
}

export const UnifiedNotificationsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const notificationsLimit = Math.max(
    25,
    Number(import.meta.env.VITE_NOTIFICATIONS_LIMIT || 100),
  );
  const notificationsTimeoutMs = Math.max(
    5000,
    Number(import.meta.env.VITE_NOTIFICATIONS_TIMEOUT_MS || 20000),
  );
  const { user } = useAuth();
  const navigate = useNavigate();
  const socketRef = useRef<Socket | null>(null);
  const sseRef = useRef<EventSource | null>(null);
  const authRecoveryRef = useRef<Promise<boolean> | null>(null);
  const fallbackTimerRef = useRef<number | null>(null);
  const sseRetryDelayRef = useRef(INITIAL_SSE_FALLBACK_DELAY_MS);
  const bootstrapPromiseRef = useRef<Promise<void> | null>(null);
  const hasBootstrappedRef = useRef(false);
  const hasConnectedOnceRef = useRef(false);
  const [notifications, setNotifications] = useState<UnifiedNotification[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connectionState, setConnectionState] =
    useState<NotificationConnectionState>("disconnected");
  const [categoryCatalog, setCategoryCatalog] = useState<
    NotificationCategoryCatalogEntry[]
  >([]);

  const fetchCategoryCatalog = useCallback(async () => {
    if (!user?.id) {
      setCategoryCatalog([]);
      return;
    }
    try {
      setCategoryCatalog(await fetchCategoryCatalogSnapshot());
    } catch {
      setCategoryCatalog([]);
    }
  }, [user?.id]);

  const refresh = useCallback(async () => {
    if (!user?.id) {
      setNotifications([]);
      return;
    }
    setIsLoading(true);
    try {
      const items = await fetchNotificationsSnapshot(
        notificationsLimit,
        notificationsTimeoutMs,
      );
      setNotifications(items);
      setError(null);
    } catch (refreshError) {
      const message =
        refreshError instanceof Error
          ? refreshError.message
          : "Failed to load notifications";
      const isTimeout = /timeout/i.test(message);

      if (isTimeout) {
        try {
          const fallbackResponse = await apiClient.get("/notifications", {
            params: { limit: 50, offset: 0, is_dismissed: false, is_read: false },
            timeout: Math.max(5000, Math.floor(notificationsTimeoutMs / 2)),
          });
          const fallbackItems = sortNotifications(
            getNotificationsArray(fallbackResponse.data),
          );
          setNotifications(fallbackItems);
          setError(null);
          return;
        } catch {
          // Fall through to default error handling.
        }
      }

      setError(
        message,
      );
    } finally {
      setIsLoading(false);
    }
  }, [notificationsLimit, notificationsTimeoutMs, user?.id]);

  const bootstrapNotifications = useCallback(async () => {
    if (!user?.id) {
      hasBootstrappedRef.current = false;
      bootstrapPromiseRef.current = null;
      setNotifications([]);
      setCategoryCatalog([]);
      return;
    }

    if (bootstrapPromiseRef.current) {
      return bootstrapPromiseRef.current;
    }

    setIsLoading(true);
    bootstrapPromiseRef.current = getOrStartNotificationBootstrap(
      user.id,
      notificationsLimit,
      notificationsTimeoutMs,
    )
      .then(({ notifications: nextNotifications, categoryCatalog }) => {
        startTransition(() => {
          setNotifications(nextNotifications);
          setCategoryCatalog(categoryCatalog);
        });
        setError(null);
      })
      .catch((bootstrapError) => {
        const message =
          bootstrapError instanceof Error
            ? bootstrapError.message
            : "Failed to load notifications";
        setError(message);
      })
      .finally(() => {
        hasBootstrappedRef.current = true;
        bootstrapPromiseRef.current = null;
        setIsLoading(false);
      });

    return bootstrapPromiseRef.current;
  }, [notificationsLimit, notificationsTimeoutMs, user?.id]);

  const markAsRead = useCallback(async (notificationId: string) => {
    if (!notificationId) return;
    setNotifications((prev) =>
      prev.map((n) =>
        n.notification_id === notificationId
          ? {
              ...n,
              is_read: true,
              read_date: new Date().toISOString(),
            }
          : n,
      ),
    );

    socketRef.current?.emit("notification:mark_read", {
      notificationId,
    });

    try {
      await apiClient.patch(`/notifications/${notificationId}/read`);
    } catch {
      await refresh();
    }
  }, [refresh]);

  const markAllAsRead = useCallback(async () => {
    setNotifications((prev) =>
      prev.map((n) => ({
        ...n,
        is_read: true,
        read_date: n.read_date || new Date().toISOString(),
      })),
    );

    socketRef.current?.emit("notification:mark_all_read");

    try {
      await apiClient.patch("/notifications/mark-all-read");
    } catch {
      await refresh();
    }
  }, [refresh]);

  const dismiss = useCallback(async (notificationId: string) => {
    if (!notificationId) return;
    setNotifications((prev) =>
      prev.map((n) =>
        n.notification_id === notificationId
          ? {
              ...n,
              is_dismissed: true,
              dismissed_date: new Date().toISOString(),
            }
          : n,
      ),
    );
    try {
      await apiClient.patch(`/notifications/${notificationId}/dismiss`);
    } catch {
      await refresh();
    }
  }, [refresh]);

  const remove = useCallback(async (notificationId: string) => {
    if (!notificationId) return;
    const previous = notifications;
    setNotifications((prev) =>
      prev.filter((n) => n.notification_id !== notificationId),
    );
    try {
      await apiClient.delete(`/notifications/${notificationId}`);
    } catch {
      setNotifications(previous);
      await refresh();
    }
  }, [notifications, refresh]);

  useEffect(() => {
    const clearFallbackTimer = () => {
      if (fallbackTimerRef.current !== null) {
        window.clearTimeout(fallbackTimerRef.current);
        fallbackTimerRef.current = null;
      }
    };

    const closeSse = () => {
      if (!sseRef.current) return;
      sseRef.current.close();
      sseRef.current = null;
    };

    if (!user?.id) {
      hasBootstrappedRef.current = false;
      hasConnectedOnceRef.current = false;
      authRecoveryRef.current = null;
      bootstrapPromiseRef.current = null;
      sseRetryDelayRef.current = INITIAL_SSE_FALLBACK_DELAY_MS;
      setNotifications([]);
      setConnectionState("disconnected");
      clearFallbackTimer();
      closeSse();
      socketRef.current?.disconnect();
      socketRef.current = null;
      return;
    }

    let isActive = true;
    const realtimeBaseUrl = normalizeRealtimeBaseUrl(resolveSocketBaseUrl());
    const debugForcedSocketToken = consumeDebugForcedSocketToken();
    let socket: Socket | null = null;
    let realtimeIdleHandle: number | null = null;
    let realtimeTimeoutHandle: number | null = null;

    const resetSseRetryDelay = () => {
      sseRetryDelayRef.current = INITIAL_SSE_FALLBACK_DELAY_MS;
    };

    const onMessage = (payload: any) => {
      if (!payload || typeof payload !== "object") return;
      if (payload.type === "notification") {
        const normalized = normalizeNotification(
          payload.notification && typeof payload.notification === "object"
            ? payload.notification
            : payload,
        );
        if (!normalized) return;
        startTransition(() =>
          setNotifications((prev) => mergeNotifications(prev, normalized)),
        );
        return;
      }
      if (payload.type === "notification_updated" && payload.id) {
        const id = String(payload.id);
        startTransition(() =>
          setNotifications((prev) =>
            prev.map((item) =>
              item.notification_id === id
                ? {
                    ...item,
                    ...(typeof payload.title === "string"
                      ? { title: payload.title }
                      : {}),
                  }
                : item,
            ),
          ),
        );
        return;
      }
      if (payload.type === "notification_read" && payload.notificationId) {
        const notificationId = String(payload.notificationId);
        startTransition(() =>
          setNotifications((prev) =>
            prev.map((item) =>
              item.notification_id === notificationId
                ? {
                    ...item,
                    is_read: true,
                    read_date: item.read_date || new Date().toISOString(),
                  }
                : item,
            ),
          ),
        );
        return;
      }
      if (payload.type === "notification_all_read") {
        startTransition(() =>
          setNotifications((prev) =>
            prev.map((item) => ({
              ...item,
              is_read: true,
              read_date: item.read_date || new Date().toISOString(),
            })),
          ),
        );
      }
    };

    const startSseFallback = () => {
      if (sseRef.current || typeof EventSource === "undefined") return;
      socket?.disconnect();

      const streamUrl = `${realtimeBaseUrl}/api/notifications/stream`;
      const sse = new EventSource(streamUrl, { withCredentials: true });
      sseRef.current = sse;

      sse.addEventListener("connected", () => {
        resetSseRetryDelay();
        setConnectionState("connected");
        setError(null);
      });

      sse.addEventListener("notification", (event) => {
        const messageEvent = event as MessageEvent<string>;
        if (!messageEvent?.data) return;
        try {
          const payload = JSON.parse(messageEvent.data);
          onMessage(payload);
          resetSseRetryDelay();
          setConnectionState("connected");
          setError(null);
        } catch {
          // Ignore malformed SSE payloads from intermediate proxies
        }
      });

      sse.addEventListener("heartbeat", () => {
        resetSseRetryDelay();
        setConnectionState("connected");
      });

      sse.onerror = () => {
        if (sseRef.current === sse) {
          sse.close();
          sseRef.current = null;
        }
        setConnectionState("disconnected");
        sseRetryDelayRef.current = Math.min(
          sseRetryDelayRef.current * 2,
          MAX_SSE_FALLBACK_DELAY_MS,
        );
        scheduleSseFallback();
      };
    };

    const scheduleSseFallback = (
      delayMs = sseRetryDelayRef.current,
    ) => {
      if (typeof EventSource === "undefined") return;
      clearFallbackTimer();
      fallbackTimerRef.current = window.setTimeout(() => {
        if (!isActive || socket?.connected) return;
        startSseFallback();
      }, delayMs);
    };

    const scheduleSocketReconnect = (
      delayMs = sseRetryDelayRef.current,
    ) => {
      clearFallbackTimer();
      fallbackTimerRef.current = window.setTimeout(() => {
        if (!isActive || socket?.connected || !socket) return;
        setConnectionState("connecting");
        socket.connect();
      }, delayMs);
    };

    const handleConnect = () => {
      clearFallbackTimer();
      closeSse();
      authRecoveryRef.current = null;
      resetSseRetryDelay();
      setConnectionState("connected");
      setError(null);
      if (!hasConnectedOnceRef.current) {
        hasConnectedOnceRef.current = true;
        if (!hasBootstrappedRef.current) {
          void bootstrapNotifications();
        }
        return;
      }
      void refresh();
    };
    const handleDisconnect = () => {
      setConnectionState("disconnected");
      scheduleSseFallback(INITIAL_SSE_FALLBACK_DELAY_MS);
    };
    const handleConnectError = (error?: Error) => {
      const message = String(error?.message || "");
      const authRejected =
        /authentication required|auth token|invalid token|invalid socket identity|authentication failed|user inactive/i.test(
          message,
        );

      if (authRejected) {
        if (!hasConnectedOnceRef.current) {
          clearFallbackTimer();
          closeSse();
          setConnectionState("disconnected");
          const retryDelayMs = sseRetryDelayRef.current;
          sseRetryDelayRef.current = Math.min(
            sseRetryDelayRef.current * 2,
            MAX_SSE_FALLBACK_DELAY_MS,
          );
          // Avoid hammering refresh + SSE during the initial post-login cookie
          // settlement window. Retry the websocket first and keep the current
          // notification snapshot visible.
          scheduleSocketReconnect(retryDelayMs);
          return;
        }

        socket?.disconnect();
        setConnectionState("connecting");

        if (!authRecoveryRef.current) {
          authRecoveryRef.current = refreshAuthenticatedSession().finally(() => {
            authRecoveryRef.current = null;
          });
        }

        void authRecoveryRef.current.then((recovered) => {
          if (!isActive) {
            return;
          }
          if (recovered) {
            clearFallbackTimer();
            closeSse();
            if (!socket) {
              scheduleSseFallback(0);
              return;
            }
            socket.auth = {
              ...(typeof socket.auth === "object" && socket.auth ? socket.auth : {}),
              userId: user.id,
            };
            setConnectionState("connecting");
            socket.connect();
            return;
          }
          clearFallbackTimer();
          closeSse();
          setConnectionState("disconnected");
          // Initial post-login websocket auth can fail transiently while
          // session/cookie state settles across redirects. Keep retrying via
          // SSE fallback instead of surfacing a sticky card-level error that
          // hides the last known notification snapshot on dashboard views.
          scheduleSseFallback(INITIAL_SSE_FALLBACK_DELAY_MS);
          if (!hasConnectedOnceRef.current) {
            return;
          }
        });
        return;
      }

      socket?.disconnect();
      setConnectionState("disconnected");
      scheduleSseFallback(INITIAL_SSE_FALLBACK_DELAY_MS);
    };

    const startRealtime = () => {
      if (!isActive) return;
      void bootstrapNotifications();
      setConnectionState("connecting");
      void import("socket.io-client")
        .then(({ io }) => {
          if (!isActive) return;
          socket = io(realtimeBaseUrl, {
            path: "/socket.io",
            transports: ["websocket", "polling"],
            withCredentials: true,
            auth: {
              userId:
                debugForcedSocketToken === "debug-force-mismatched-user"
                  ? `debug-mismatch-${user.id}`
                  : user.id,
            },
            reconnection: false,
          });
          socketRef.current = socket;

          socket.on("connect", handleConnect);
          socket.on("disconnect", handleDisconnect);
          socket.on("connect_error", handleConnectError);
          socket.on("message", onMessage);
          socket.on("notification", onMessage);
          scheduleSseFallback();
        })
        .catch(() => {
          if (!isActive) return;
          setConnectionState("disconnected");
          scheduleSseFallback(INITIAL_SSE_FALLBACK_DELAY_MS);
        });
    };

    // Keep provider mounted (no remount) but defer socket/bootstrap off LCP.
    const ric = (
      window as Window & {
        requestIdleCallback?: (
          cb: IdleRequestCallback,
          opts?: IdleRequestOptions,
        ) => number;
        cancelIdleCallback?: (id: number) => void;
      }
    ).requestIdleCallback;
    if (typeof ric === "function") {
      realtimeIdleHandle = ric(() => startRealtime(), { timeout: 2500 });
    } else {
      realtimeTimeoutHandle = window.setTimeout(startRealtime, 1);
    }

    return () => {
      isActive = false;
      if (realtimeIdleHandle != null) {
        (
          window as Window & {
            cancelIdleCallback?: (id: number) => void;
          }
        ).cancelIdleCallback?.(realtimeIdleHandle);
      }
      if (realtimeTimeoutHandle != null) {
        window.clearTimeout(realtimeTimeoutHandle);
      }
      clearFallbackTimer();
      closeSse();
      socket?.off("connect", handleConnect);
      socket?.off("disconnect", handleDisconnect);
      socket?.off("connect_error", handleConnectError);
      socket?.off("message", onMessage);
      socket?.off("notification", onMessage);
      socket?.disconnect();
      if (socketRef.current === socket && socket) {
        socketRef.current = null;
      }
      if (authRecoveryRef.current) {
        authRecoveryRef.current = null;
      }
    };
  }, [bootstrapNotifications, fetchCategoryCatalog, refresh, user?.id]);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n.is_read && !n.is_dismissed).length,
    [notifications],
  );

  const criticalCount = useMemo(
    () =>
      notifications.filter(
        (n) => !n.is_read && !n.is_dismissed && n.priority === "critical",
      ).length,
    [notifications],
  );

  const countsByCategory = useMemo(() => {
    return notifications.reduce<Record<string, number>>((acc, notification) => {
      const key = notification.category || notification.type || "system";
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }, [notifications]);

  const reportCategories = useMemo(
    () =>
      categoryCatalog
        .filter((entry) => entry.is_active && entry.is_report_category)
        .map((entry) => entry.category),
    [categoryCatalog],
  );

  const activeCriticalNotification = useMemo(() => {
    return notifications
      .filter(
        (n) =>
          !n.is_read &&
          !n.is_dismissed &&
          n.priority === "critical" &&
          n.action_required,
      )
      .sort(
        (a, b) =>
          new Date(b.created_date || 0).getTime() -
          new Date(a.created_date || 0).getTime(),
      )[0];
  }, [notifications]);

  const value = useMemo<UnifiedNotificationsContextValue>(
    () => ({
      notifications,
      unreadCount,
      criticalCount,
      countsByCategory,
      categoryCatalog,
      reportCategories,
      isLoading,
      error,
      connectionState,
      refresh,
      markAsRead,
      markAllAsRead,
      dismiss,
      remove,
    }),
    [
      notifications,
      unreadCount,
      criticalCount,
      countsByCategory,
      categoryCatalog,
      reportCategories,
      isLoading,
      error,
      connectionState,
      refresh,
      markAsRead,
      markAllAsRead,
      dismiss,
      remove,
    ],
  );

  return (
    <UnifiedNotificationsContext.Provider value={value}>
      {children}
      {activeCriticalNotification ? (
        <Suspense fallback={null}>
          <CriticalNotificationModal
            notification={activeCriticalNotification}
            onAcknowledge={(notificationId) => {
              void markAsRead(notificationId);
            }}
            onOpen={(notification) => {
              void markAsRead(notification.notification_id);
              if (notification.action_url) {
                navigate(notification.action_url);
              }
            }}
          />
        </Suspense>
      ) : null}
    </UnifiedNotificationsContext.Provider>
  );
};

export function useUnifiedNotifications(): UnifiedNotificationsContextValue {
  const context = useContext(UnifiedNotificationsContext);
  if (!context) {
    throw new Error(
      "useUnifiedNotifications must be used within UnifiedNotificationsProvider",
    );
  }
  return context;
}
